<template>
  <div class="wallet-system">
    <!-- Balance Overview -->
    <div class="balance-overview">
      <h2><i class="fas fa-wallet"></i> Wallet & Balances</h2>

      <div class="balance-cards">
        <!-- Wallet Card (Always visible) -->
        <div class="balance-card wallet-card">
          <div class="card-icon">
            <i class="fas fa-wallet"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Wallet Balance</p>
            <h3 class="card-amount">{{ formatAmount(balances.wallet_balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Business Balance Card (Only if has business account) -->
        <div v-if="balances.has_business_account" class="balance-card business-card">
          <div class="card-icon">
            <i class="fas fa-briefcase"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Business Suite</p>
            <h3 class="card-amount">{{ formatAmount(balances.business_balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Marketer Campaign Card (Only if has marketer campaign) -->
        <div v-if="balances.has_marketer_campaign && balances.marketer_campaign" class="balance-card marketer-card">
          <div class="card-icon">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Marketer Balance</p>
            <h3 class="card-amount">{{ formatAmount(balances.marketer_campaign.balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Principal Account Card (Always visible if exists) -->
        <div v-if="balances.principal_campaign" class="balance-card principal-card">
          <div class="card-icon">
            <i class="fas fa-piggy-bank"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Principal Account</p>
            <h3 class="card-amount">{{ formatAmount(balances.principal_campaign.balance) }} <span>FCFA</span></h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Tabs -->
    <div class="action-tabs">
      <button
        :class="['tab-btn', { active: activeTab === 'transfer' }]"
        @click="activeTab = 'transfer'"
      >
        <i class="fas fa-exchange-alt"></i> Send Money
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'deposit' }]"
        @click="activeTab = 'deposit'"
      >
        <i class="fas fa-arrow-down"></i> Deposit to Wallet
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'withdraw' }]"
        @click="activeTab = 'withdraw'"
      >
        <i class="fas fa-piggy-bank"></i> Save to Principal Account
      </button>
    </div>

    <!-- Transfer Money (From Wallet to Another User) -->
    <div v-if="activeTab === 'transfer'" class="action-section">
      <div class="section-card" :class="{ 'confirming': transferState.isConfirming }">
        <h3><i class="fas fa-paper-plane"></i> Send Money</h3>
        <p class="info-text">Transfer from your wallet to another user's principal account</p>

        <div v-if="!transferState.isConfirming" class="form-section">
          <div class="input-group">
            <label><i class="fas fa-user"></i> Recipient</label>
            <input
              v-model="transferState.recipientIdentifier"
              type="text"
              placeholder="Phone number or ID (e.g., 656569928)"
              @keyup.enter="verifyRecipient"
            >
            <p class="hint">Enter phone number or serial ID</p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount (FCFA)</label>
            <input
              v-model.number="transferState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount"
            >
            <p class="hint">Available in wallet: {{ formatAmount(balances.wallet_balance) }} FCFA</p>
          </div>

          <button @click="verifyRecipient" :disabled="loading" class="primary-btn">
            <i class="fas fa-check-circle"></i>
            {{ loading ? 'Verifying...' : 'Verify Recipient' }}
          </button>
        </div>

        <div v-else class="confirmation-section">
          <div class="confirmation-header">
            <h4><i class="fas fa-shield-alt"></i> Confirm Transfer</h4>
            <p>Please verify the details below</p>
          </div>

          <div class="recipient-info-card">
            <div class="recipient-avatar">
              <i class="fas fa-user-circle"></i>
            </div>
            <div class="recipient-details">
              <p class="recipient-name">{{ transferState.recipientDetails.username }}</p>
              <p v-if="transferState.recipientDetails.phone_number">
                <i class="fas fa-phone"></i> {{ transferState.recipientDetails.phone_number }}
              </p>
              <p v-if="transferState.recipientDetails.serial_no">
                <i class="fas fa-id-card"></i> {{ transferState.recipientDetails.serial_no }}
              </p>
            </div>
          </div>

          <div class="amount-display">
            <p>Transfer Amount</p>
            <div class="amount-value">{{ formatAmount(transferState.amount) }} <span>FCFA</span></div>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="transferState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="confirmTransfer"
            >
          </div>

          <div class="button-group">
            <button @click="confirmTransfer" :disabled="loading" class="confirm-btn">
              <i class="fas fa-paper-plane"></i>
              {{ loading ? 'Processing...' : 'Confirm Transfer' }}
            </button>
            <button @click="cancelTransfer" :disabled="loading" class="cancel-btn">
              <i class="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Deposit to Wallet -->
    <div v-if="activeTab === 'deposit'" class="action-section">
      <div class="section-card">
        <h3><i class="fas fa-arrow-down"></i> Deposit to Wallet</h3>
        <p class="info-text">Move funds from your accounts to your wallet</p>

        <div class="form-section">
          <div class="input-group">
            <label><i class="fas fa-source"></i> Source Account</label>
            <select v-model="depositState.sourceType" @change="validateDepositAmount">
              <option value="">Select source...</option>
              <option v-if="balances.has_business_account" value="BUSINESS_BALANCE">
                Business Suite ({{ formatAmount(balances.business_balance) }} FCFA)
              </option>
              <option v-if="balances.has_marketer_campaign && balances.marketer_campaign" value="MARKETER_CAMPAIGN">
                Marketer Balance ({{ formatAmount(balances.marketer_campaign.balance) }} FCFA)
              </option>
              <option v-if="balances.principal_campaign" value="PRINCIPAL_CAMPAIGN">
                Principal Account ({{ formatAmount(balances.principal_campaign.balance) }} FCFA)
              </option>
            </select>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount (FCFA)</label>
            <input
              v-model.number="depositState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount"
              @input="validateDepositAmount"
            >

            <!-- Principal Account WARNING -->
            <div v-if="depositState.sourceType === 'PRINCIPAL_CAMPAIGN'" class="principal-warning-box">
              <div class="warning-header">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Principal Account Minimum Balance</span>
              </div>
              <div class="warning-content">
                <p><strong>Current Balance:</strong> {{ formatAmount(balances.principal_campaign.balance) }} FCFA</p>
                <p><strong>Minimum Required:</strong> 2,000 FCFA</p>
                <p><strong>Maximum Withdrawable:</strong> {{ formatAmount(getMaxWithdrawableFromPrincipal()) }} FCFA</p>
              </div>
              <p v-if="depositState.amount > getMaxWithdrawableFromPrincipal()" class="error-text">
                ⚠️ Cannot withdraw. Principal account must maintain minimum 2,000 FCFA!
              </p>
            </div>

            <p v-else class="hint">
              Enter amount to transfer to wallet
            </p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="depositState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="depositToWallet"
            >
          </div>

          <button
            @click="depositToWallet"
            :disabled="loading || !isDepositValid"
            class="primary-btn"
            :class="{ 'disabled-btn': !isDepositValid }"
          >
            <i class="fas fa-arrow-down"></i>
            {{ loading ? 'Processing...' : 'Deposit to Wallet' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Withdraw from Wallet - UNIQUEMENT vers Principal Account -->
    <div v-if="activeTab === 'withdraw'" class="action-section">
      <div class="section-card principal-save-card">
        <h3><i class="fas fa-piggy-bank"></i> Save to Principal Account</h3>
        <p class="info-text">
          <i class="fas fa-info-circle"></i>
          Move funds from your wallet to your principal account
        </p>

        <!-- Info box explaining the restriction -->
        <div class="principal-info-box">
          <div class="info-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div class="info-content">
            <h4>Why only Principal Account?</h4>
            <p>For security and financial discipline, wallet withdrawals can only be transferred to your Principal Account. This ensures your funds remain secure and working towards your long-term goals.</p>
          </div>
        </div>

        <div class="form-section">
          <!-- Destination fixée à Principal Account -->
          <div class="input-group">
            <label><i class="fas fa-piggy-bank"></i> Destination Account</label>
            <div class="fixed-destination">
              <div class="destination-icon">
                <i class="fas fa-piggy-bank"></i>
              </div>
              <div class="destination-info">
                <p class="destination-name">Principal Account</p>
                <p class="destination-balance">
                  Current balance: {{ formatAmount(balances.principal_campaign?.balance || 0) }} FCFA
                </p>
              </div>
              <div class="destination-badge">
                <i class="fas fa-lock"></i> Secured
              </div>
            </div>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount to Save (FCFA)</label>
            <input
              v-model.number="withdrawState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount to save"
            >
            <p class="hint">Available in wallet: {{ formatAmount(balances.wallet_balance) }} FCFA</p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="withdrawState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="saveToPrincipal"
            >
          </div>

          <!-- Preview du nouveau solde -->
          <div v-if="withdrawState.amount > 0" class="balance-preview">
            <div class="preview-item">
              <span class="preview-label">
                <i class="fas fa-wallet"></i> Wallet after transfer
              </span>
              <span class="preview-value">{{ formatAmount(balances.wallet_balance - withdrawState.amount) }} FCFA</span>
            </div>
            <div class="preview-divider">
              <i class="fas fa-arrow-down"></i>
            </div>
            <div class="preview-item highlight">
              <span class="preview-label">
                <i class="fas fa-piggy-bank"></i> Principal Account after transfer
              </span>
              <span class="preview-value success">
                {{ formatAmount((balances.principal_campaign?.balance || 0) + withdrawState.amount) }} FCFA
              </span>
            </div>
          </div>

          <button @click="saveToPrincipal" :disabled="loading" class="primary-btn save-btn">
            <i class="fas fa-piggy-bank"></i>
            {{ loading ? 'Processing...' : 'Save to Principal Account' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div v-if="message" :class="['alert-message', messageType]">
      <i :class="messageType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
      <span>{{ message }}</span>
      <button @click="message = ''" class="close-alert">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template><template>
  <div class="wallet-system">
    <!-- Balance Overview -->
    <div class="balance-overview">
      <h2><i class="fas fa-wallet"></i> Wallet & Balances</h2>

      <div class="balance-cards">
        <!-- Wallet Card (Always visible) -->
        <div class="balance-card wallet-card">
          <div class="card-icon">
            <i class="fas fa-wallet"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Wallet Balance</p>
            <h3 class="card-amount">{{ formatAmount(balances.wallet_balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Business Balance Card (Only if has business account) -->
        <div v-if="balances.has_business_account" class="balance-card business-card">
          <div class="card-icon">
            <i class="fas fa-briefcase"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Business Suite</p>
            <h3 class="card-amount">{{ formatAmount(balances.business_balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Marketer Campaign Card (Only if has marketer campaign) -->
        <div v-if="balances.has_marketer_campaign && balances.marketer_campaign" class="balance-card marketer-card">
          <div class="card-icon">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Marketer Balance</p>
            <h3 class="card-amount">{{ formatAmount(balances.marketer_campaign.balance) }} <span>FCFA</span></h3>
          </div>
        </div>

        <!-- Principal Account Card (Always visible if exists) -->
        <div v-if="balances.principal_campaign" class="balance-card principal-card">
          <div class="card-icon">
            <i class="fas fa-piggy-bank"></i>
          </div>
          <div class="card-content">
            <p class="card-label">Principal Account</p>
            <h3 class="card-amount">{{ formatAmount(balances.principal_campaign.balance) }} <span>FCFA</span></h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Tabs -->
    <div class="action-tabs">
      <button
        :class="['tab-btn', { active: activeTab === 'transfer' }]"
        @click="activeTab = 'transfer'"
      >
        <i class="fas fa-exchange-alt"></i> Send Money
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'deposit' }]"
        @click="activeTab = 'deposit'"
      >
        <i class="fas fa-arrow-down"></i> Deposit to Wallet
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'withdraw' }]"
        @click="activeTab = 'withdraw'"
      >
        <i class="fas fa-piggy-bank"></i> Save to Principal Account
      </button>
    </div>

    <!-- Transfer Money (From Wallet to Another User) -->
    <div v-if="activeTab === 'transfer'" class="action-section">
      <div class="section-card" :class="{ 'confirming': transferState.isConfirming }">
        <h3><i class="fas fa-paper-plane"></i> Send Money</h3>
        <p class="info-text">Transfer from your wallet to another user's principal account</p>

        <div v-if="!transferState.isConfirming" class="form-section">
          <div class="input-group">
            <label><i class="fas fa-user"></i> Recipient</label>
            <input
              v-model="transferState.recipientIdentifier"
              type="text"
              placeholder="Phone number or ID (e.g., 656569928)"
              @keyup.enter="verifyRecipient"
            >
            <p class="hint">Enter phone number or serial ID</p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount (FCFA)</label>
            <input
              v-model.number="transferState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount"
            >
            <p class="hint">Available in wallet: {{ formatAmount(balances.wallet_balance) }} FCFA</p>
          </div>

          <button @click="verifyRecipient" :disabled="loading" class="primary-btn">
            <i class="fas fa-check-circle"></i>
            {{ loading ? 'Verifying...' : 'Verify Recipient' }}
          </button>
        </div>

        <div v-else class="confirmation-section">
          <div class="confirmation-header">
            <h4><i class="fas fa-shield-alt"></i> Confirm Transfer</h4>
            <p>Please verify the details below</p>
          </div>

          <div class="recipient-info-card">
            <div class="recipient-avatar">
              <i class="fas fa-user-circle"></i>
            </div>
            <div class="recipient-details">
              <p class="recipient-name">{{ transferState.recipientDetails.username }}</p>
              <p v-if="transferState.recipientDetails.phone_number">
                <i class="fas fa-phone"></i> {{ transferState.recipientDetails.phone_number }}
              </p>
              <p v-if="transferState.recipientDetails.serial_no">
                <i class="fas fa-id-card"></i> {{ transferState.recipientDetails.serial_no }}
              </p>
            </div>
          </div>

          <div class="amount-display">
            <p>Transfer Amount</p>
            <div class="amount-value">{{ formatAmount(transferState.amount) }} <span>FCFA</span></div>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="transferState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="confirmTransfer"
            >
          </div>

          <div class="button-group">
            <button @click="confirmTransfer" :disabled="loading" class="confirm-btn">
              <i class="fas fa-paper-plane"></i>
              {{ loading ? 'Processing...' : 'Confirm Transfer' }}
            </button>
            <button @click="cancelTransfer" :disabled="loading" class="cancel-btn">
              <i class="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Deposit to Wallet -->
    <div v-if="activeTab === 'deposit'" class="action-section">
      <div class="section-card">
        <h3><i class="fas fa-arrow-down"></i> Deposit to Wallet</h3>
        <p class="info-text">Move funds from your accounts to your wallet</p>

        <div class="form-section">
          <div class="input-group">
            <label><i class="fas fa-source"></i> Source Account</label>
            <select v-model="depositState.sourceType" @change="validateDepositAmount">
              <option value="">Select source...</option>
              <option v-if="balances.has_business_account" value="BUSINESS_BALANCE">
                Business Suite ({{ formatAmount(balances.business_balance) }} FCFA)
              </option>
              <option v-if="balances.has_marketer_campaign && balances.marketer_campaign" value="MARKETER_CAMPAIGN">
                Marketer Balance ({{ formatAmount(balances.marketer_campaign.balance) }} FCFA)
              </option>
              <option v-if="balances.principal_campaign" value="PRINCIPAL_CAMPAIGN">
                Principal Account ({{ formatAmount(balances.principal_campaign.balance) }} FCFA)
              </option>
            </select>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount (FCFA)</label>
            <input
              v-model.number="depositState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount"
              @input="validateDepositAmount"
            >

            <!-- Principal Account WARNING -->
            <div v-if="depositState.sourceType === 'PRINCIPAL_CAMPAIGN'" class="principal-warning-box">
              <div class="warning-header">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Principal Account Minimum Balance</span>
              </div>
              <div class="warning-content">
                <p><strong>Current Balance:</strong> {{ formatAmount(balances.principal_campaign.balance) }} FCFA</p>
                <p><strong>Minimum Required:</strong> 2,000 FCFA</p>
                <p><strong>Maximum Withdrawable:</strong> {{ formatAmount(getMaxWithdrawableFromPrincipal()) }} FCFA</p>
              </div>
              <p v-if="depositState.amount > getMaxWithdrawableFromPrincipal()" class="error-text">
                ⚠️ Cannot withdraw. Principal account must maintain minimum 2,000 FCFA!
              </p>
            </div>

            <p v-else class="hint">
              Enter amount to transfer to wallet
            </p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="depositState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="depositToWallet"
            >
          </div>

          <button
            @click="depositToWallet"
            :disabled="loading || !isDepositValid"
            class="primary-btn"
            :class="{ 'disabled-btn': !isDepositValid }"
          >
            <i class="fas fa-arrow-down"></i>
            {{ loading ? 'Processing...' : 'Deposit to Wallet' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Withdraw from Wallet - UNIQUEMENT vers Principal Account -->
    <div v-if="activeTab === 'withdraw'" class="action-section">
      <div class="section-card principal-save-card">
        <h3><i class="fas fa-piggy-bank"></i> Save to Principal Account</h3>
        <p class="info-text">
          <i class="fas fa-info-circle"></i>
          Move funds from your wallet to your principal account
        </p>

        <!-- Info box explaining the restriction -->
        <div class="principal-info-box">
          <div class="info-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div class="info-content">
            <h4>Why only Principal Account?</h4>
            <p>For security and financial discipline, wallet withdrawals can only be transferred to your Principal Account. This ensures your funds remain secure and working towards your long-term goals.</p>
          </div>
        </div>

        <div class="form-section">
          <!-- Destination fixée à Principal Account -->
          <div class="input-group">
            <label><i class="fas fa-piggy-bank"></i> Destination Account</label>
            <div class="fixed-destination">
              <div class="destination-icon">
                <i class="fas fa-piggy-bank"></i>
              </div>
              <div class="destination-info">
                <p class="destination-name">Principal Account</p>
                <p class="destination-balance">
                  Current balance: {{ formatAmount(balances.principal_campaign?.balance || 0) }} FCFA
                </p>
              </div>
              <div class="destination-badge">
                <i class="fas fa-lock"></i> Secured
              </div>
            </div>
          </div>

          <div class="input-group">
            <label><i class="fas fa-money-bill-wave"></i> Amount to Save (FCFA)</label>
            <input
              v-model.number="withdrawState.amount"
              type="number"
              min="100"
              step="100"
              placeholder="Enter amount to save"
            >
            <p class="hint">Available in wallet: {{ formatAmount(balances.wallet_balance) }} FCFA</p>
          </div>

          <div class="input-group">
            <label><i class="fas fa-lock"></i> Confirm Password</label>
            <input
              v-model="withdrawState.password"
              type="password"
              placeholder="Enter your password"
              @keyup.enter="saveToPrincipal"
            >
          </div>

          <!-- Preview du nouveau solde -->
          <div v-if="withdrawState.amount > 0" class="balance-preview">
            <div class="preview-item">
              <span class="preview-label">
                <i class="fas fa-wallet"></i> Wallet after transfer
              </span>
              <span class="preview-value">{{ formatAmount(balances.wallet_balance - withdrawState.amount) }} FCFA</span>
            </div>
            <div class="preview-divider">
              <i class="fas fa-arrow-down"></i>
            </div>
            <div class="preview-item highlight">
              <span class="preview-label">
                <i class="fas fa-piggy-bank"></i> Principal Account after transfer
              </span>
              <span class="preview-value success">
                {{ formatAmount((balances.principal_campaign?.balance || 0) + withdrawState.amount) }} FCFA
              </span>
            </div>
          </div>

          <button @click="saveToPrincipal" :disabled="loading" class="primary-btn save-btn">
            <i class="fas fa-piggy-bank"></i>
            {{ loading ? 'Processing...' : 'Save to Principal Account' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div v-if="message" :class="['alert-message', messageType]">
      <i :class="messageType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
      <span>{{ message }}</span>
      <button @click="message = ''" class="close-alert">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WalletSystem',
  data() {
    return {
      activeTab: 'transfer',
      loading: false,
      message: '',
      messageType: '',
      balances: {
        wallet_balance: 0,
        business_balance: null,
        principal_campaign: null,
        marketer_campaign: null,
        has_business_account: false,
        has_marketer_campaign: false
      },
      transferState: {
        recipientIdentifier: '',
        amount: 0,
        password: '',
        isConfirming: false,
        recipientDetails: null
      },
      depositState: {
        sourceType: '',
        amount: 0,
        password: ''
      },
      withdrawState: {
        amount: 0,
        password: ''
      }
    };
  },

  computed: {
    isDepositValid() {
      if (!this.depositState.sourceType || this.depositState.amount <= 0) {
        return false;
      }

      if (this.depositState.sourceType === 'PRINCIPAL_CAMPAIGN') {
        const maxWithdrawable = this.getMaxWithdrawableFromPrincipal();
        return this.depositState.amount <= maxWithdrawable;
      }

      return true;
    }
  },

  async created() {
    await this.fetchBalances();
  },

  methods: {
    async fetchBalances() {
      try {
        const response = await axios.get('/api/v1/savings/wallet/balances/');
        this.balances = response.data;
      } catch (error) {
        console.error('❌ Error fetching balances:', error);
        this.showError('Failed to load balances');
      }
    },

    getMaxWithdrawableFromPrincipal() {
      if (!this.balances.principal_campaign) return 0;

      const MIN_BALANCE = 2000;
      const currentBalance = parseInt(this.balances.principal_campaign.balance) || 0;
      const maxWithdrawable = currentBalance - MIN_BALANCE;

      return maxWithdrawable > 0 ? maxWithdrawable : 0;
    },

    validateDepositAmount() {
      if (this.depositState.sourceType === 'PRINCIPAL_CAMPAIGN') {
        const maxWithdrawable = this.getMaxWithdrawableFromPrincipal();
        const currentAmount = parseInt(this.depositState.amount) || 0;

        if (currentAmount > maxWithdrawable) {
          console.log("❌ Amount too high!");
        }
      }
    },

    async verifyRecipient() {
      if (!this.transferState.recipientIdentifier || this.transferState.amount <= 0) {
        this.showError('Please fill all fields correctly');
        return;
      }

      if (this.balances.wallet_balance < this.transferState.amount) {
        this.showError('Insufficient wallet balance');
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const response = await axios.post('/api/v1/savings/wallet/transfer/', {
          recipient_identifier: this.transferState.recipientIdentifier,
          amount: this.transferState.amount
        });

        this.transferState.recipientDetails = response.data.recipient;
        this.transferState.isConfirming = true;
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Recipient not found';
        this.showError(errorMsg);
      } finally {
        this.loading = false;
      }
    },

    async confirmTransfer() {
      if (!this.transferState.password) {
        this.showError('Password is required');
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const response = await axios.post('/api/v1/savings/wallet/transfer/', {
          recipient_identifier: this.transferState.recipientIdentifier,
          amount: this.transferState.amount,
          is_confirmed: true,
          password: this.transferState.password
        });

        this.showSuccess(`Transfer successful! Reference: ${response.data.payment_ref}`);
        this.resetTransferState();
        await this.fetchBalances();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Transfer failed';
        this.showError(errorMsg);
      } finally {
        this.loading = false;
      }
    },

    cancelTransfer() {
      this.resetTransferState();
      this.message = '';
    },

    resetTransferState() {
      this.transferState = {
        recipientIdentifier: '',
        amount: 0,
        password: '',
        isConfirming: false,
        recipientDetails: null
      };
    },

    async depositToWallet() {
      if (!this.depositState.sourceType || this.depositState.amount <= 0 || !this.depositState.password) {
        this.showError('Please fill all fields correctly');
        return;
      }

      if (this.depositState.sourceType === 'PRINCIPAL_CAMPAIGN') {
        const maxWithdrawable = this.getMaxWithdrawableFromPrincipal();
        if (this.depositState.amount > maxWithdrawable) {
          this.showError(`Cannot withdraw. Maximum withdrawable: ${this.formatAmount(maxWithdrawable)} FCFA (Must keep 2,000 FCFA minimum)`);
          return;
        }
      }

      this.loading = true;
      this.message = '';

      try {
        const response = await axios.post('/api/v1/savings/wallet/deposit/', {
          source_type: this.depositState.sourceType,
          amount: this.depositState.amount,
          password: this.depositState.password
        });

        this.showSuccess(response.data.message);
        this.depositState = { sourceType: '', amount: 0, password: '' };
        await this.fetchBalances();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Deposit failed';
        this.showError(errorMsg);
      } finally {
        this.loading = false;
      }
    },

    // ✅ NOUVELLE MÉTHODE: Enregistrer uniquement vers Principal
    async saveToPrincipal() {
      if (this.withdrawState.amount <= 0 || !this.withdrawState.password) {
        this.showError('Please fill all fields correctly');
        return;
      }

      if (this.balances.wallet_balance < this.withdrawState.amount) {
        this.showError('Insufficient wallet balance');
        return;
      }

      if (!this.balances.principal_campaign) {
        this.showError('Principal campaign not found');
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const response = await axios.post('/api/v1/savings/wallet/withdraw/', {
          destination_type: 'PRINCIPAL_CAMPAIGN',
          amount: this.withdrawState.amount,
          password: this.withdrawState.password
        });

        this.showSuccess(response.data.message);
        this.withdrawState = { amount: 0, password: '' };
        await this.fetchBalances();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Transfer to Principal failed';
        this.showError(errorMsg);
      } finally {
        this.loading = false;
      }
    },

    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0';
      return parseFloat(amount).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },

    showError(message) {
      this.messageType = 'error';
      this.message = message;
      setTimeout(() => { this.message = ''; }, 5000);
    },

    showSuccess(message) {
      this.messageType = 'success';
      this.message = message;
      setTimeout(() => { this.message = ''; }, 5000);
    }
  }
};
</script>

<style scoped>
/* ... Tous les styles précédents ... */
.wallet-system {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.balance-overview h2 {
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 25px;
  font-size: 1.8rem;
}

.balance-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.balance-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.3s, box-shadow 0.3s;
  border: 2px solid transparent;
}

.balance-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.wallet-card { border-color: #3498db; }
.business-card { border-color: #9b59b6; }
.marketer-card { border-color: #e67e22; }
.principal-card { border-color: #27ae60; }

.card-icon {
  font-size: 2.5rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.wallet-card .card-icon { background: #e8f4fe; color: #3498db; }
.business-card .card-icon { background: #f4e8fe; color: #9b59b6; }
.marketer-card .card-icon { background: #fef0e8; color: #e67e22; }
.principal-card .card-icon { background: #e8fef0; color: #27ae60; }

.card-content {
  flex: 1;
}

.card-label {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-bottom: 8px;
  font-weight: 500;
}

.card-amount {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.card-amount span {
  font-size: 1rem;
  color: #95a5a6;
  font-weight: 500;
}

/* ✅ NOUVEAUX STYLES pour la section Principal */
.principal-save-card {
  border: 2px solid #27ae60;
  background: linear-gradient(135deg, #ffffff 0%, #f0fff4 100%);
}

.principal-info-box {
  display: flex;
  gap: 15px;
  background: linear-gradient(135deg, #d4edda 0%, #e8f5e9 100%);
  border: 2px solid #27ae60;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
}

.info-icon {
  font-size: 2.5rem;
  color: #27ae60;
}

.info-content h4 {
  color: #155724;
  margin-bottom: 10px;
  font-size: 1.1rem;
}

.info-content p {
  color: #155724;
  line-height: 1.6;
  font-size: 0.95rem;
}

.fixed-destination {
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(135deg, #e8fef0 0%, #f0fff4 100%);
  padding: 20px;
  border-radius: 12px;
  border: 2px solid #27ae60;
}

.destination-icon {
  font-size: 2.5rem;
  color: #27ae60;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 12px;
}

.destination-info {
  flex: 1;
}

.destination-name {
  font-weight: 700;
  font-size: 1.2rem;
  color: #155724;
  margin-bottom: 5px;
}

.destination-balance {
  color: #27ae60;
  font-size: 0.9rem;
}

.destination-badge {
  background: #27ae60;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.balance-preview {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border: 2px solid #e1e8f0;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}

.preview-label {
  color: #5a6c7d;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e50;
}

.preview-value.success {
  color: #27ae60;
}

.preview-divider {
  text-align: center;
  color: #95a5a6;
  margin: 10px 0;
}

.preview-item.highlight {
  background: linear-gradient(135deg, #e8fef0 0%, #f0fff4 100%);
  padding: 15px;
  border-radius: 8px;
  margin-top: 10px;
}

.save-btn {
  background: linear-gradient(135deg, #27ae60, #229954) !important;
}

.save-btn:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(39, 174, 96, 0.4) !important;
}

/* Autres styles existants... */
.action-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.tab-btn {
  flex: 1;
  min-width: 150px;
  padding: 15px 20px;
  border: 2px solid #e1e8f0;
  background: white;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: #7f8c8d;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-btn:hover {
  border-color: #3498db;
  color: #3498db;
  transform: translateY(-2px);
}

.tab-btn.active {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border-color: #3498db;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.section-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.section-card.confirming {
  box-shadow: 0 8px 25px rgba(52, 152, 219, 0.2);
  border: 2px solid #3498db;
}

.section-card h3 {
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.info-text {
  color: #7f8c8d;
  margin-bottom: 25px;
  font-size: 0.95rem;
}

.form-section {
  margin-top: 20px;
}

.input-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #34495e;
  display: flex;
  align-items: center;
  gap: 8px;
}

input, select {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e1e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  background: #f8fafc;
  font-family: inherit;
}

input:focus, select:focus {
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  background: white;
  outline: none;
}

.hint {
  font-size: 0.85rem;
  color: #95a5a6;
  margin-top: 6px;
}

.hint.warning {
  color: #e67e22;
  font-weight: 500;
}

button {
  padding: 15px 25px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: inherit;
}

.primary-btn {
  width: 100%;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.primary-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(52, 152, 219, 0.4);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirmation-section {
  margin-top: 20px;
}

.confirmation-header {
  text-align: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px dashed #e1e8f0;
}

.confirmation-header h4 {
  color: #2c3e50;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 1.3rem;
}

.confirmation-header p {
  color: #7f8c8d;
  font-size: 0.95rem;
}

.recipient-info-card {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #f8fafc;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e1e8f0;
}

.recipient-avatar i {
  font-size: 3.5rem;
  color: #3498db;
}

.recipient-details {
  flex: 1;
}

.recipient-name {
  font-weight: 700;
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 8px;
}

.recipient-details p {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5a6c7d;
  margin: 5px 0;
  font-size: 0.95rem;
}

.amount-display {
  text-align: center;
  margin: 25px 0;
  padding: 20px;
  background: linear-gradient(135deg, #e8f4fe, #f0f7ff);
  border-radius: 12px;

}



.principal-warning-box {
  background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 16px;
  margin-top: 12px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #856404;
  margin-bottom: 12px;
  font-size: 1rem;
}

.warning-header i {
  font-size: 1.2rem;
  color: #ff9800;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.warning-content {
  background: rgba(255, 255, 255, 0.8);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid #ffc107;
}

.warning-content p {
  margin: 8px 0;
  color: #856404;
  font-size: 0.9rem;
  line-height: 1.5;
}

.warning-content strong {
  color: #664d03;
  font-weight: 700;
}

.error-text {
  color: #dc3545 !important;
  font-weight: 700 !important;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  padding: 10px;
  background: #f8d7da;
  border-radius: 8px;
  border: 1px solid #dc3545;
}

.disabled-btn {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: #95a5a6 !important;
  pointer-events: none;
}

.disabled-btn:hover {
  transform: none !important;
  box-shadow: none !important;
}

.button-group {
  display: flex;
  gap: 12px;
  margin-top: 15px;
}

.button-group button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: 0.25s ease;
  font-size: 14px;
}

/* Bouton CONFIRMER */
.confirm-btn {
  background: #0d6efd;
  color: #fff;
  box-shadow: 0 3px 8px rgba(13,110,253,0.25);
}

.confirm-btn:hover:not(:disabled) {
  background: #0b5ed7;
  box-shadow: 0 4px 12px rgba(13,110,253,0.35);
  transform: translateY(-1px);
}

/* Bouton ANNULER */
.cancel-btn {
  background: #f1f1f1;
  color: #333;
}

.cancel-btn:hover:not(:disabled) {
  background: #e4e4e4;
  transform: translateY(-1px);
}

/* État disabled */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

</style>